import { useState } from "react";
import { ChevronDown, Check, Star } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../client/components/ui/popover";
import { cn } from "../../client/utils";
import useTranslation from "../../client/hooks/useTranslation";

const PHASE_STATUSES = ["challenge", "pa", "live"] as const;
type PhaseStatus = typeof PHASE_STATUSES[number];

interface TopbarPhaseFilterWidgetProps {
  accounts: any[];
  accountStatusFilter: string | null;
  onAccountStatusFilterChange: (status: string | null) => void;
  pinnedValue?: string | null;
  onPinValue?: (status: string | null) => void;
}

interface RowProps {
  label: string;
  isSelected: boolean;
  isPinned: boolean;
  onSelect: () => void;
  onPin?: () => void;
}

function FilterRow({ label, isSelected, isPinned, onSelect, onPin }: RowProps) {
  return (
    <div
      className={cn(
        "group flex items-center gap-1 rounded-sm px-2 py-1.5 text-sm cursor-pointer select-none",
        "hover:bg-accent hover:text-accent-foreground",
        isSelected && "bg-accent/50"
      )}
      onClick={onSelect}
    >
      <Check className={cn("h-3.5 w-3.5 shrink-0 mr-1", isSelected ? "opacity-100" : "opacity-0")} />
      <span className="flex-1 truncate">{label}</span>
      {onPin && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPin(); }}
          className={cn(
            "flex-shrink-0 flex h-5 w-5 items-center justify-center rounded transition-colors",
            "opacity-0 group-hover:opacity-100",
            isPinned ? "opacity-100 text-yellow-400 hover:text-yellow-500" : "text-muted-foreground hover:text-yellow-400"
          )}
        >
          <Star className={cn("h-3 w-3", isPinned && "fill-yellow-400")} />
        </button>
      )}
    </div>
  );
}

export default function TopbarPhaseFilterWidget({
  accounts,
  accountStatusFilter,
  onAccountStatusFilterChange,
  pinnedValue,
  onPinValue,
}: TopbarPhaseFilterWidgetProps) {
  const t = useTranslation();
  const [open, setOpen] = useState(false);

  const presentStatuses = new Set(
    (accounts as any[])
      .map((a) => a.status as string)
      .filter((s): s is PhaseStatus => PHASE_STATUSES.includes(s as PhaseStatus))
  );

  if (presentStatuses.size === 0) return null;

  const statusLabels: Record<PhaseStatus, string> = {
    challenge: t.dashboard.widgets.topbarPhaseFilterChallenge,
    pa: t.dashboard.widgets.topbarPhaseFilterPa,
    live: t.dashboard.widgets.topbarPhaseFilterLive,
  };

  const allLabel = t.dashboard.widgets.topbarPhaseFilterAll;
  const triggerPlaceholder = t.dashboard.widgets.topbarPhaseFilter;
  const selectedLabel = accountStatusFilter
    ? (statusLabels[accountStatusFilter as PhaseStatus] ?? triggerPlaceholder)
    : triggerPlaceholder;

  const select = (status: string | null) => {
    onAccountStatusFilterChange(status);
    setOpen(false);
  };

  const isActive = accountStatusFilter !== null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center justify-between gap-2 rounded-md border px-3 font-medium text-sm transition-colors",
            isActive
              ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
              : "bg-background text-foreground border-border hover:bg-accent hover:text-accent-foreground",
            "w-[200px] h-9"
          )}
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-1 max-h-[400px] overflow-y-auto" align="start">
        <FilterRow
          label={allLabel}
          isSelected={accountStatusFilter === null}
          isPinned={pinnedValue !== undefined && pinnedValue === null}
          onSelect={() => select(null)}
          onPin={onPinValue ? () => onPinValue(null) : undefined}
        />
        {PHASE_STATUSES.filter((s) => presentStatuses.has(s)).map((status) => (
          <FilterRow
            key={status}
            label={statusLabels[status]}
            isSelected={accountStatusFilter === status}
            isPinned={pinnedValue !== null && pinnedValue !== undefined && pinnedValue === status}
            onSelect={() => select(status)}
            onPin={onPinValue ? () => onPinValue(status) : undefined}
          />
        ))}
      </PopoverContent>
    </Popover>
  );
}
